import { Box, Typography, Avatar } from '@mui/material';
import {
  HomeWorkOutlined,
  FormatListNumberedOutlined,
  LocationCityOutlined,
  LocalParkingOutlined,
  MeetingRoomOutlined,
  EventAvailableOutlined,
  CalendarMonthOutlined,
  CheckCircleOutline,
  SquareFootOutlined,
} from '@mui/icons-material';
import { ReactNode, useEffect, useMemo, useState } from 'react';

// Column wrapper for flex layout (responsive: 1col < 900px, 2col >= 900px)
const InfoCol = ({ children }) => {
  return (
    <Box
      sx={{
        flex: '1 1 340px',
        minWidth: { xs: '100%', md: 0 },
        px: 2,
        mb: { xs: 2, md: 0 },
      }}
    >
      {children}
    </Box>
  );
};

// Row: icon + label + value
export const InfoRow = ({ icon, label, value }: { icon?: any; label: string; value: any }) => {
  return (
    <Box display="flex" alignItems="center" gap={2} mb={1.2}>
      {icon && (
        <Box
          sx={{
            minWidth: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: (theme) => theme.palette.grey[500],
          }}
        >
          {icon}
        </Box>
      )}
      <Box>
        <Typography
          variant="body1"
          color="#8D8D8D"
          sx={{ fontSize: { xs: '1rem', sm: '1.2rem', lg: '1.4rem' }, fontWeight: 400 }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '1.35rem', sm: '1.55rem', lg: '1.75rem' },
            color: (theme) => theme.palette.primary.main,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const UnitSizeRow = ({ type, series, size, unit }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 0.5, md: 2 },
        mb: 0.5,
        textAlign: 'left',
        py: 0.5,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          mr: 4,
          minWidth: { sm: 250, md: 350 },
          width: { xs: '100%', sm: 'auto' },
          fontSize: { xs: '1.35rem', sm: '1.55rem', lg: '1.75rem' },
        }}
      >
        {type} {series}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: { xs: '1.35rem', sm: '1.55rem', lg: '1.75rem' } }}>
        <Box component={'span'} sx={{ color: (theme) => theme.palette.primary.main, pr: 2 }}>
          {size}
        </Box>{' '}
        {unit}
      </Typography>
    </Box>
  );
};

export const ProjectInfo = ({ projectInfo, units, t, lang }) => {

  return (
    <Box>
      {/* Main info rows */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          mx: -2,
        }}
      >
        <InfoCol>
          <InfoRow
            icon={<HomeWorkOutlined sx={{ fontSize: { xs: 32, sm: 48 } }} />}
            label={t('project-type')}
            value={`${projectInfo?.category} ${projectInfo?.type}` || t('not-specified')}
          />
          <InfoRow
            icon={<LocationCityOutlined sx={{ fontSize: { xs: 32, sm: 48 } }} />}
            label={t('land-size')}
            value={projectInfo[`Land_Size_${lang}`] || t('not-specified')}
          />
          <InfoRow
            icon={<MeetingRoomOutlined sx={{ fontSize: { xs: 32, sm: 48 } }} />}
            label={t('tenure')}
            value={projectInfo[`Tenure_${lang}`] || t('not-specified')}
          />
          <InfoRow
            icon={<CalendarMonthOutlined sx={{ fontSize: { xs: 32, sm: 48 } }} />}
            label={t('completed')}
            value={projectInfo[`Expeted_Completed_${lang}`] || t('not-specified')}
          />
        </InfoCol>
        <InfoCol>
          <InfoRow
            icon={<FormatListNumberedOutlined sx={{ fontSize: { xs: 32, sm: 48 } }} />}
            label={t('quantity')}
            value={
              <span>
                {projectInfo?.No_of_Floor_TH ? `${projectInfo?.No_of_Floor_TH}` : ''}
                <Box component="span" sx={{ color: (theme) => theme.palette.common.black }}>
                  {projectInfo?.No_of_Floor_TH ? ` ${t('floor-unit')} ` : null}
                </Box>
                {projectInfo?.No_of_Building ? `${projectInfo?.No_of_Building}` : ''}
                <Box component="span" sx={{ color: (theme) => theme.palette.common.black }}>
                  {projectInfo?.No_of_Building ? ` ${t('building-unit')} ` : null}
                </Box>
                {projectInfo?.No_of_Unit ? `${projectInfo?.No_of_Unit}` : ''}
                <Box component="span" sx={{ color: (theme) => theme.palette.common.black }}>
                  {projectInfo?.No_of_Unit ? ` ${t('unit')} ` : null}
                </Box>
              </span>
            }
          />
          <InfoRow
            icon={<LocalParkingOutlined sx={{ fontSize: { xs: 32, sm: 48 } }} />}
            label={t('parking')}
            value={
              <span>
                {projectInfo?.No_of_Parking || t('not-specified')}
                <Box component="span" sx={{ color: (theme) => theme.palette.common.black }}>
                  {projectInfo?.No_of_Parking ? ` ${t('car-unit')}` : null}
                </Box>
              </span>
            }
          />
          <InfoRow
            icon={<EventAvailableOutlined sx={{ fontSize: { xs: 32, sm: 48 } }} />}
            label={t('Status')}
            value={<span>{projectInfo?.status || t('not-specified')}</span>}
          />
        </InfoCol>
      </Box>

      {/* Room Size Section */}
      <Box>
        <InfoRow
          icon={<SquareFootOutlined sx={{ fontSize: { xs: 32, sm: 48 } }} />}
          label={t('room-size')}
          value={''}
        />
        <Box
          sx={{
            marginLeft: { xs: 7, sm: 8 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {units?.map((item, index) => (
            <Box key={index}>
              <UnitSizeRow {...item} />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Developer Section */}
      <Box display="flex" alignItems="center" gap={2} my={3} ml={1}>
        {projectInfo?.developed_image && (
          <Avatar
            src={projectInfo?.developed_image}
            variant="rounded"
            sx={{
              width: 54,
              height: 54,
              bgcolor: '#fff',
              boxShadow: '0px 2px 10px 0px #0000001A',
            }}
          />
        )}
        <InfoRow
          label={t('developed-by')}
          value={<span style={{ color: '#000' }}>{projectInfo?.developed || '-'}</span>}
        />
      </Box>
    </Box>
  );
};
